import { createRoot } from 'react-dom/client'
import { ICollectionLink } from '../components'

export const DEFAULT_MENU_HANDLE: string = 'default-menu'

export interface EnvironmentMap<T> {
  dev: T
  prod: T
}

interface EnvOptions extends EnvironmentMap<boolean> {}

export interface SiteConfig {
  theme: ThemeSettings
  env: EnvOptions
  flags: FeatureFlags
}

/**
 * Feature Flags as initialized in 'head-scripts.liquid'
 */
interface FeatureFlags {
  isCollectionBannerEnabled: boolean
  logging: boolean
  isMaintenanceModeEnabled: boolean
}

interface ThemeSettings {
  accentColor: string
  assets: AssetConfig
}

interface AssetConfig {
  path: string
  version: string
}

interface SiteUrlConfig {
  url: EnvironmentMap<string>
  shop: EnvironmentMap<string>
}

declare const siteConfig: SiteConfig

/**
 * Mount a React component.
 *
 * @param {React.ReactElement<{}>} component
 * @param {string} domNodeId
 * @return {void}
 */
export const mount = (component: React.ReactElement<{}>, domNodeId: string): void => {
  const nodes: NodeListOf<HTMLElement> = document.querySelectorAll(domNodeId)
  Array.from(nodes).forEach((node: HTMLElement) => {
    createRoot(node).render(component)
  })
}

/**
 * Retrieve a value safely from the window object.
 *
 * @param {string} key
 * @param {any} defaultValue
 */
export const retrieveFromWindow: Function = (key: string, defaultValue: any = null): any => {
  const value: any = (window as any)[key]
  return value ? value : defaultValue
}

/**
 * Attempt to use smooth scroll to test whether the browser supports it.
 */
function testSupportSmoothScroll(): boolean {
  let support: boolean = false

  try {
    const div: HTMLDivElement = document.createElement('div')
    div.scrollTo({
      top: 0,
      get behavior(): ScrollBehavior {
        support = true
        return 'smooth'
      }
    })
  } catch (err) {}

  return support
}

/**
 * Use the browser supported scrollTo method
 *
 * @param x
 * @param y
 * @param behavior
 */
export function scrollTo(x: number, y: number, behavior: ScrollBehavior = 'auto'): void {
  if (testSupportSmoothScroll()) {
    window.scrollTo({
      left: x,
      top: y,
      behavior
    })
  } else {
    window.scrollTo(x, y)
  }
}

export function getAssetUrl(fileName: string, size: string = ''): string {
  const { path, version } = siteConfig.theme.assets

  if (size) {
    // If the size is specified, rework the filename to grab the correct size
    const pos: number = fileName.indexOf('.')
    const sizedFile: string = `${fileName.substr(0, pos)}_${size}${fileName.substr(pos)}`

    return `${path}${sizedFile}?${version}`
  }

  return `${path}${fileName}?${version}`
}

/**
 * Retrieve the status of a feature flag.
 *
 * @param flag Name of the feature flag
 * @returns feature flag status
 *
 * @example
 * ```tsx
 * const isMyFeatureEnabled = getFeatureFlag('isMyFeatureEnabled')
 *
 * if( isMyFeatureEnabled ) {
 *   <MyFeature />
 * } else {
 *   // do nothing
 * }
 * ```
 *
 * - You can create a new flag in 'config/settings_schema.json'
 * - Initialize the flag in 'head-scripts.liquid'
 * - Add the flag to the FeatureFlags interface
 */
export function getFeatureFlag(flag: keyof FeatureFlags): boolean {
  return (siteConfig && siteConfig.flags[flag]) || false
}

export function isDefaultMenuHandle(link: ICollectionLink): boolean {
  return link?.handle?.startsWith(DEFAULT_MENU_HANDLE)
}
